import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
 ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { GtwSnackbarService } from '../../_services/gtw-snackbar.service';

export interface ModalLockPayload {
  isLocked: boolean;
  message: string;
  checkRequired: boolean;
}

export interface CeHostDialogData {
  ceTagName: string;
  rowData: any;
  colData: any;
  headerData: any;
  baseUrls: any;
  filterParams: any;
  gridData?: any;
  modalName?: string;
  modalDesc?: string;
  lock?: ModalLockPayload;
  /**
   * Column metadata (array of `{label, map, ...}`) from the originating grid.
   * Mirrors the legacy AngularJS template contract where modal templates passed
   * `header-label="{{ctrl.headers | json}}"` so the hosted custom element could
   * build its form layout from the grid's own headers. Used by components like
   * `gtw-efile-edit-submission-table-data-ce` which render their form based on
   * whichever columns the grid currently has.
   */
  headerLabel?: any;
  /**
   * Optional `template-mode` attribute value, propagated from the
   * `ComponentRegistryService` entry. Used by shared custom elements that
   * switch layouts based on the modal variant (e.g. `gtw-entity-chart-map-ce`
   * renders chartmap / entity-edit / update depending on this value).
   */
  templateMode?: string;
  /**
   * Optional extra attributes to set on the custom element, keyed by attribute
   * name. Values are JSON-serialized if they are objects/arrays.
   * Used for sub-components with non-standard attribute APIs (e.g. gtw-section-details-ce).
   */
  extraAttributes?: Record<string, any>;
}

@Component({
    selector: 'gtw-ce-host-dialog',
    template: `
    <div class="ce-host-dialog-wrapper">
      <div #ceContainer class="ce-host-container"></div>
    </div>
  `,
    styles: [`
    /*
     * Full flex-column chain from the MatDialog body down to the custom element,
     * so that hosted CE components can scroll their own .modal-body when the
     * dialog has a bounded height (e.g. dialogHeight: 90vh in the registry).
     * Without the chain every level collapses to content height and the body
     * overflows the dialog with no scrollbar. The custom element itself is
     * styled inline inside createCustomElement below.
     */
    :host {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .ce-host-dialog-wrapper {
      width: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .ce-host-container {
      width: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CeHostDialogComponent implements OnInit, OnDestroy {
  @ViewChild('ceContainer', { static: true }) ceContainer!: ElementRef;

  private ceElement: HTMLElement | null = null;
  private eventCleanups: (() => void)[] = [];
  private previousActiveScreenData: any;
  private lockObserver: MutationObserver | null = null;
  private lockDecoratedHeaders = new WeakSet<HTMLElement>();
  private lockDecoratedBodies = new WeakSet<HTMLElement>();
  private lockDecoratedButtons = new WeakSet<HTMLButtonElement>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CeHostDialogData,
    private dialogRef: MatDialogRef<CeHostDialogComponent>,
    private renderer: Renderer2,
    private workspaceService: WorkspaceService,
    private gtwSnackbarService: GtwSnackbarService,
    private matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.bridgeDataToActiveScreen();
    this.createCustomElement();
    this.applyLockStateIfNeeded();
  }

  /**
   * Migrated components read context from WorkspaceService.activeScreen.data
   * instead of @Input HTML attributes. Bridge the dialog data into activeScreen
   * so those components find rowData, gridData, modal_name, header (column
   * metadata), etc.
   *
   * NOTE on `header`: the existing `activeScreen.data` is spread first, which
   * preserves the grid's own `.header` entry (written by `grid.directive`
   * during the last grid load). We also park `this.data.headerLabel` under the
   * same key as an explicit fallback — when the dialog is opened from a screen
   * that doesn't populate `activeScreen.data.header` directly (edge case), the
   * column metadata still reaches the custom element via the dialog payload.
   */
  private bridgeDataToActiveScreen(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    if (!activeScreen) return;

    this.previousActiveScreenData = activeScreen.data;

    const callBack = this.data.modalName
      || this.data.colData?.call_back
      || '';

    const ccList = this.extractCcList();
    const filingGroup = this.extractFilingGroup();

    const gridData = this.data.gridData;
    const rowData = this.data.rowData;
    const isEmptyPayload = (value: any): boolean => {
      if (value == null) {
        return true;
      }
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      if (typeof value === 'object') {
        return Object.keys(value).length === 0;
      }
      return false;
    };
    const isAddMode = isEmptyPayload(gridData) && isEmptyPayload(rowData);
    const preservedActionTemplates = activeScreen.data?.actionTemplates;
    const preserveGridSelection =
      callBack === 'gridActionModal' || callBack === 'gridPivotActionModal';

    activeScreen.data = {
      ...activeScreen.data,
      header: activeScreen.data?.header ?? this.data.headerLabel,
      selected: preserveGridSelection ? activeScreen.data?.selected : undefined,
      rowData: isAddMode ? {} : this.data.rowData,
      colData: this.data.colData,
      gridData: this.data.gridData,
      modal_name: callBack,
      modalName: callBack,
      ccList,
      filingGroup,
      processName: 'UPDATE_ENTITY_CHART_PROCESS',
      checkLockedBj: [],
    };

    if (Array.isArray(preservedActionTemplates) && preservedActionTemplates.length > 0) {
      activeScreen.data.actionTemplates = preservedActionTemplates;
    }
  }

  private extractCcList(): string {
    const rowKey = this.data.rowData?.COMBINATION_KEY;
    if (rowKey != null && rowKey !== '') return String(rowKey);
    const gridData = this.data.gridData;
    const rows = Array.isArray(gridData)
      ? gridData
      : Array.isArray(gridData?.gridData)
        ? gridData.gridData
        : [];
    const keys = rows
      .map((r: any) => r?.data?.COMBINATION_KEY ?? r?.COMBINATION_KEY)
      .filter((v: any) => v != null && v !== '');
    return keys.join(',');
  }

  private extractFilingGroup(): number {
    const raw = this.data.filterParams?.filing_key
      ?? this.data.rowData?.FILING_KEY
      ?? this.data.rowData?.filing_key;
    const n = parseInt(String(raw ?? '0'), 10);
    return Number.isFinite(n) ? n : 0;
  }

  private createCustomElement(): void {
    const { ceTagName, rowData, colData, headerData, baseUrls, filterParams, gridData, headerLabel } = this.data;

    if (!customElements.get(ceTagName)) {
      console.warn(`[CeHostDialog] Custom element "${ceTagName}" is not registered. The component will not render.`);
    }

    this.ceElement = document.createElement(ceTagName);
    this.ceElement.style.display = 'block';
    this.ceElement.style.width = '100%';
    this.ceElement.classList.add('view-animate', 'my-ease-in');
    // Close the flex-column chain from the dialog shell down to the custom
    // element. The CE is created via `document.createElement`, so no Angular
    // CSS selector can target it from outside. Inline flex styles provide a
    // column container for modal-header / modal-body / modal-footer without
    // forcing height: 100% (which stretched modals to the viewport and clipped
    // footers). Components that need a scrollable body set
    // `flex: 1; overflow-y: auto; min-height: 0` on their own `.modal-body`.
    this.ceElement.style.display = 'flex';
    this.ceElement.style.flexDirection = 'column';
    this.ceElement.style.flex = '1 1 auto';
    this.ceElement.style.minHeight = '0';
    this.ceElement.style.width = '100%';

    if (rowData != null) {
      this.ceElement.setAttribute('row-data', JSON.stringify(rowData));
    }
    if (colData != null) {
      this.ceElement.setAttribute('col-data', JSON.stringify(colData));
    }
    if (headerData != null) {
      this.ceElement.setAttribute('header-data', JSON.stringify(headerData));
    }
    if (baseUrls != null) {
      this.ceElement.setAttribute('base-urls', JSON.stringify(baseUrls));
    }
    if (filterParams != null) {
      this.ceElement.setAttribute('filter-params', JSON.stringify(filterParams));
    }
    if (gridData != null) {
      this.ceElement.setAttribute('grid-data', JSON.stringify(gridData));
    }
    // Bridge the grid's column metadata so modal-form components (e.g.
    // `gtw-efile-edit-submission-table-data-ce`) can build their field list
    // from whichever columns the grid actually has. Without this the modal
    // renders empty because the `@Input('header-label')` setter never fires.
    if (headerLabel != null) {
      this.ceElement.setAttribute('header-label', JSON.stringify(headerLabel));
    }

    const modalName = this.data.modalName || this.data.colData?.call_back || '';
    if (modalName) {
      this.ceElement.setAttribute('modal-name', modalName);
    }

    if (this.data.modalDesc) {
      this.ceElement.setAttribute('modal-desc', this.data.modalDesc);
    }

    if (this.data.templateMode) {
      this.ceElement.setAttribute('template-mode', this.data.templateMode);
    }

    if (this.data.extraAttributes) {
      for (const [attrName, attrValue] of Object.entries(this.data.extraAttributes)) {
        this.ceElement.setAttribute(
          attrName,
          typeof attrValue === 'object' ? JSON.stringify(attrValue) : String(attrValue)
        );
      }
    }

    if (this.data.lock?.isLocked) {
      this.ceElement.setAttribute('modal-lock', 'Y');
      if (this.data.lock.message) {
        this.ceElement.setAttribute('modal-lock-message', this.data.lock.message);
      }
    }

    this.listenForEvents(this.ceElement);

    this.renderer.appendChild(this.ceContainer.nativeElement, this.ceElement);
  }

  /**
   * Port of legacy gridController.js `modalInstance.rendered` lock decoration.
   * When the modal payload reports `lock_check_required === 'Y'` and
   * `is_locked === 'Y'`:
   *  - decorate `.modal-header` with `modal-lock-hdr-class` (padlock badge);
   *  - decorate `.modal-body` with `modal-lock-body-class` (read-only overlay);
   *  - disable every `.modal-footer .btn` / `button`;
   *  - surface the backend message via a red snackbar.
   *
   * The CE markup is shadowed/async so we mount a MutationObserver to catch
   * late insertions and re-apply the decoration idempotently.
   */
  private applyLockStateIfNeeded(): void {
    if (!this.data.lock?.isLocked || !this.ceElement) {
      return;
    }

    this.renderer.addClass(this.ceContainer.nativeElement, 'modal-window-lock-class');

    // NOTE: we deliberately do NOT surface the backend `lockResult.lock_message`
    // as a Material snackbar here. AngularJS shows that message via a 3-second
    // `AlertService.add("error", …)` in a small legacy alert bar; our Material
    // snackbar renders it as a top-center red banner that overlaps the modal
    // for 4 seconds, which felt like a spurious toast compared to staging.
    // The lock state is already communicated visually via `modal-window-lock-class`
    // on the dialog panel (gray-out / padlock on classic layouts) so the
    // snackbar was pure noise. Parity with staging for non-classic
    // confirmation modals where the AngularJS alert is barely noticeable.

    const decorate = () => this.decorateLockedDom();
    decorate();
    setTimeout(decorate, 50);
    setTimeout(decorate, 250);

    this.lockObserver = new MutationObserver(() => decorate());
    this.lockObserver.observe(this.ceElement, {
      childList: true,
      subtree: true,
    });
  }

  private decorateLockedDom(): void {
    if (!this.ceElement) return;

    // --- Classic modal layout (.modal-header / .modal-body / .modal-footer) ---
    //
    // Skip decoration when the match lives inside a `<gtw-confirmation-modal>`.
    // That shared component (used by fir163jsync, aggregration-rerun,
    // consolcalcSchJRerun, consolcalcCreditsRerun, aggSecondaryAction and
    // other admin sync/rerun actions) renders `.modal-header` + `.msg-title`
    // in a "big orange Confirm" layout — but the legacy AngularJS gridController
    // never decorates those modals (on staging `.modal-header` isn't even in
    // DOM because the deployed bundle keeps `showModal=false`; staging therefore
    // runs into the silent null.classList.add path). Local Angular 12 code
    // flips showModal=true, so `.modal-header` IS rendered and would get
    // wrongly decorated with the padlock. Gating on the confirmation-modal
    // ancestor gives visual parity with staging without reverting the showModal
    // change and without starving real classic modals of the lock badge.
    const isInsideConfirmationModal = (el: HTMLElement): boolean =>
      !!el.closest('gtw-confirmation-modal');

    const headers = this.ceElement.querySelectorAll<HTMLElement>('.modal-header');
    headers.forEach((header) => {
      if (isInsideConfirmationModal(header)) return;
      if (!this.lockDecoratedHeaders.has(header)) {
        header.classList.add('modal-lock-hdr-class');
        this.lockDecoratedHeaders.add(header);
      }
    });

    const bodies = this.ceElement.querySelectorAll<HTMLElement>('.modal-body');
    bodies.forEach((body) => {
      if (isInsideConfirmationModal(body)) return;
      if (!this.lockDecoratedBodies.has(body)) {
        body.classList.add('modal-lock-body-class');
        this.lockDecoratedBodies.add(body);
      }
    });

    // Match AngularJS `gridController.js` selector: `[class*="btn"]`.
    // That substring matches legacy Bootstrap classes (`btn`, `btn-primary`,
    // `btn-danger`, …) but does NOT match `ap-button` / `ap-button-primary`
    // from Appkit4, whose class tokens only contain the full word "button".
    // The previous generic `button` selector over-matched and disabled the
    // inner <button> that `<ap-button>` renders, making Cancel/Yes unclickable
    // on sync confirmation modals (fx-amt-sync, fx-rates-sync, spotrates).
    const footerButtons = this.ceElement.querySelectorAll<HTMLButtonElement>(
      '.modal-footer .btn, .modal-footer [class*="btn"]'
    );
    footerButtons.forEach((btn) => {
      if (isInsideConfirmationModal(btn)) return;
      if (!this.lockDecoratedButtons.has(btn)) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        this.lockDecoratedButtons.add(btn);
      }
    });

    // --- Confirmation-modal layout: intentionally NOT decorated ---
    // AngularJS `gridController.js:1249` only decorates `.modal-header`:
    //   modalLockSelector.querySelector('.modal-header').classList.add(...)
    // When no `.modal-header` exists (confirmation modals with just
    // `.div-message-box`, plain MatDialog sync confirmations, etc.) the legacy
    // code calls `null.classList.add(...)` which throws silently inside the
    // `$scope.openModal` callback — so no padlock, no body overlay, no disabled
    // buttons. We mirror that behavior literally: if there is no `.modal-header`
    // we apply NO inner lock decoration. The lock state is still communicated
    // via `modal-window-lock-class` on the MatDialog panel (see grid.directive
    // and applyLockStateIfNeeded) so interoperability with CSS hooks is kept.
    //
    // Verified at runtime against staging 163j-sync:
    //   staging: containerClasses="modal modal-window-lock-class", #switch-yes
    //            disabled=false, opacity=1
    //   localhost (post-fix): same.
  }

  private listenForEvents(el: HTMLElement): void {
    const onGridUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('[CeHostDialog] request-grid-update received:', detail);

      if (detail === 'cancel') {
        this.dialogRef.close({ action: 'close' });
      } else if (detail && typeof detail === 'object' && detail.openSource) {
        this.dialogRef.close({
          action: 'refresh',
          openSource: true,
          tagKey: detail.tagKey,
        });
      } else {
        this.dialogRef.close({ action: 'refresh', detail });
      }
    };

    const onClose = (_event: Event) => {
      console.log('[CeHostDialog] close-modal received');
      this.dialogRef.close({ action: 'close' });
    };

    const onSave = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('[CeHostDialog] save-and-close received:', detail);
      this.dialogRef.close({ action: 'refresh', detail });
    };

    const onOpenSectionDetails = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      const { dataRow, rowData, sourceRow } = detail;
      this.matDialog.open(CeHostDialogComponent, {
        width: '60%',
        maxHeight: '90vh',
        data: {
          ceTagName: 'gtw-section-details-ce',
          rowData: rowData || this.data.rowData,
          colData: null,
          headerData: null,
          baseUrls: this.data.baseUrls,
          filterParams: this.data.filterParams,
          extraAttributes: {
            'data-row': dataRow || {},
            'source-row': sourceRow || {}
          }
        } as CeHostDialogData,
        disableClose: true,
        panelClass: 'gtw-dialog-panel'
      });
    };

    el.addEventListener('request-grid-update', onGridUpdate);
    el.addEventListener('close-modal', onClose);
    el.addEventListener('save-and-close', onSave);
    el.addEventListener('open-section-details', onOpenSectionDetails);

    this.eventCleanups.push(
      () => el.removeEventListener('request-grid-update', onGridUpdate),
      () => el.removeEventListener('close-modal', onClose),
      () => el.removeEventListener('save-and-close', onSave),
      () => el.removeEventListener('open-section-details', onOpenSectionDetails)
    );
  }

  ngOnDestroy(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    if (activeScreen && this.previousActiveScreenData !== undefined) {
      const callBack = this.data.modalName || this.data.colData?.call_back || '';
      const isGridActionModal =
        callBack === 'gridActionModal' || callBack === 'gridPivotActionModal';
      const updatedActionTemplates = isGridActionModal
        ? activeScreen.data?.actionTemplates
        : undefined;

      activeScreen.data = this.previousActiveScreenData;

      // GridActionCtrl saves actionTemplates on the bridged activeScreen.data;
      // restoring the pre-open snapshot must not drop that refreshed list.
      if (isGridActionModal && Array.isArray(updatedActionTemplates)) {
        activeScreen.data.actionTemplates = updatedActionTemplates;
      }
    }

    this.eventCleanups.forEach((fn) => fn());
    this.eventCleanups = [];

    if (this.lockObserver) {
      this.lockObserver.disconnect();
      this.lockObserver = null;
    }

    if (this.ceElement && this.ceContainer?.nativeElement?.contains(this.ceElement)) {
      this.renderer.removeChild(this.ceContainer.nativeElement, this.ceElement);
    }
    this.ceElement = null;
  }
}
